// src/utils/multiTabSync.js
// *******************************************************************************
// MULTI-TAB SESSION SYNCHRONIZATION (using BroadcastChannel API)
// *******************************************************************************
// This utility ensures session consistency across browser tabs using the modern
// BroadcastChannel API for direct tab-to-tab communication.
//
// Behavior:
// - Logout in any tab → other tabs do forced logout with "session expired" message
// - Login in tab1 → other tabs on login page stay unchanged
// - Login in tab2 with different account → tab1 (logged in) does forced logout
// *******************************************************************************

import app from 'configs/appResolver';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const CHANNEL_NAME = `${app.ID}:auth-sync-channel`;

// Event types
const EVENT_LOGOUT = 'LOGOUT';
const EVENT_LOGIN = 'LOGIN';
const EVENT_USER_CHANGE = 'USER_CHANGE';

// *******************************************************************************
// INTERNAL STATE
// *******************************************************************************
let channel = null;
let currentUserId = null;
let isInitialized = false;
let onForceLogoutCallback = null;
let onUserChangeCallback = null;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Generate a unique tab ID (persisted per tab session)
 */
const getTabId = () => {
  if (typeof sessionStorage === 'undefined') return null;

  let tabId = sessionStorage.getItem('tabId');
  if (!tabId) {
    tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('tabId', tabId);
  }
  return tabId;
};

/**
 * Check if we're on an auth page (login, register, etc.)
 */
const isOnAuthPage = () => {
  try {
    const prefix = app.AUTH_PREFIX_PATH || '/auth';
    return window.location.pathname.startsWith(prefix);
  } catch {
    return false;
  }
};

// *******************************************************************************
// BROADCAST CHANNEL MESSAGE HANDLER
// *******************************************************************************

/**
 * Handle messages from other tabs
 */
const handleMessage = (event) => {
  const { type, userId, tabId, timestamp } = event.data;

  // Ignore messages from this same tab
  if (tabId === getTabId()) return;

  switch (type) {
    case EVENT_LOGOUT:
      handleLogoutFromOtherTab();
      break;

    case EVENT_LOGIN:
      handleLoginFromOtherTab(userId);
      break;

    case EVENT_USER_CHANGE:
      handleUserChangeFromOtherTab(userId);
      break;

    default:
      break;
  }
};

/**
 * Handle logout event from another tab
 * Only affects tabs that are logged in (not on auth pages)
 */
const handleLogoutFromOtherTab = () => {
  // If we're already on auth page, do nothing
  if (isOnAuthPage()) return;

  // If we're logged in, force logout with expiration message
  if (currentUserId) {
    if (onForceLogoutCallback) {
      onForceLogoutCallback({
        reason: 'other_tab_logout',
        showExpiredMessage: true,
      });
    }
  }
};

/**
 * Handle login event from another tab
 * Only affects tabs that are logged in with a DIFFERENT user
 */
const handleLoginFromOtherTab = (newUserId) => {
  // If we're on auth page, do nothing (let user login normally)
  if (isOnAuthPage()) return;

  // If we're logged in with a DIFFERENT user, force logout
  if (currentUserId && currentUserId !== newUserId) {
    if (onForceLogoutCallback) {
      onForceLogoutCallback({
        reason: 'different_user_login',
        newUserId,
        showExpiredMessage: true,
      });
    }
  }
};

/**
 * Handle user change event from another tab
 */
const handleUserChangeFromOtherTab = (newUserId) => {
  // If we're on auth page, do nothing
  if (isOnAuthPage()) return;

  // If we're logged in with a different user, force logout
  if (currentUserId && currentUserId !== newUserId) {
    if (onForceLogoutCallback) {
      onForceLogoutCallback({
        reason: 'user_change',
        newUserId,
        showExpiredMessage: true,
      });
    }
  }
};

// *******************************************************************************
// PUBLIC API
// *******************************************************************************

/**
 * Initialize multi-tab synchronization with BroadcastChannel
 * @param {Object} options - Configuration options
 * @param {Function} options.onForceLogout - Callback when forced logout is needed
 * @param {Function} options.onUserChange - Callback when user changes (optional)
 */
export const initMultiTabSync = (options = {}) => {
  if (isInitialized) return;
  if (typeof window === 'undefined') return;

  // Check BroadcastChannel support
  if (typeof BroadcastChannel === 'undefined') {
    initStorageFallback(options);
    return;
  }

  // Store callbacks
  onForceLogoutCallback = options.onForceLogout || null;
  onUserChangeCallback = options.onUserChange || null;

  // Create broadcast channel
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = handleMessage;
  isInitialized = true;
};

/**
 * Fallback to storage events for older browsers
 */
const initStorageFallback = (options) => {
  const STORAGE_KEY = `${app.ID}:session-sync-fallback`;

  onForceLogoutCallback = options.onForceLogout || null;

  const handleStorage = (event) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;

    try {
      const data = JSON.parse(event.newValue);
      handleMessage({ data });
    } catch {}
  };

  window.addEventListener('storage', handleStorage);

  // Override broadcast to use localStorage
  channel = {
    postMessage: (data) => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, timestamp: Date.now() }));
      // Clear immediately to allow same message to be sent again
      setTimeout(() => localStorage.removeItem(STORAGE_KEY), 100);
    },
    close: () => window.removeEventListener('storage', handleStorage),
  };

  isInitialized = true;
};

/**
 * Clean up multi-tab synchronization
 */
export const destroyMultiTabSync = () => {
  if (channel) {
    channel.close();
    channel = null;
  }

  currentUserId = null;
  onForceLogoutCallback = null;
  onUserChangeCallback = null;
  isInitialized = false;
};

/**
 * Notify other tabs about logout
 * Called when user explicitly logs out
 */
export const notifyLogout = () => {
  if (!channel) return;

  channel.postMessage({
    type: EVENT_LOGOUT,
    userId: null,
    tabId: getTabId(),
    timestamp: Date.now(),
  });

  currentUserId = null;
};

/**
 * Notify other tabs about login
 * @param {string} userId - The logged in user ID
 */
export const notifyLogin = (userId) => {
  if (!channel || !userId) return;

  channel.postMessage({
    type: EVENT_LOGIN,
    userId,
    tabId: getTabId(),
    timestamp: Date.now(),
  });

  currentUserId = userId;
};

/**
 * Notify other tabs about user change
 * @param {string} newUserId - The new user ID
 */
export const notifyUserChange = (newUserId) => {
  if (!channel || !newUserId) return;

  channel.postMessage({
    type: EVENT_USER_CHANGE,
    userId: newUserId,
    tabId: getTabId(),
    timestamp: Date.now(),
  });

  currentUserId = newUserId;
};

/**
 * Set the current user ID (called during app initialization)
 * @param {string} userId - The current user ID
 */
export const setCurrentUserId = (userId) => {
  currentUserId = userId;
};

/**
 * Get the current user ID
 * @returns {string|null}
 */
export const getCurrentUserId = () => currentUserId;

/**
 * Check if multi-tab sync is initialized
 * @returns {boolean}
 */
export const isMultiTabSyncInitialized = () => isInitialized;

// *******************************************************************************
// DEFAULT EXPORT
// *******************************************************************************
const multiTabSync = {
  init: initMultiTabSync,
  destroy: destroyMultiTabSync,
  notifyLogin,
  notifyLogout,
  notifyUserChange,
  setCurrentUserId,
  getCurrentUserId,
  isInitialized: isMultiTabSyncInitialized,
};

export default multiTabSync;
